import { PromptResultCreate } from '@common/types';
import { groupBy, isArray, isNil, isString } from 'lodash';
import { ExtractedDataFields, useMutatePromptResult } from 'hooks';
import { isNumeric } from 'utils';

interface PromptResultsReportParams {
  formState: Partial<Record<string, any>>;
  suggestedValueProps: ExtractedDataFields;
  submissionId: string;
  entityId: string;
  getValue?: (formValue: any) => any;
}

// 🔧 Normalize and sort an object recursively
function normalizeAndSortObject(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(normalizeAndSortObject);
  }

  if (obj && typeof obj === 'object') {
    return Object.keys(obj)
      .filter((key) => obj[key] !== null && obj[key] !== '')
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeAndSortObject(obj[key]);
        return acc;
      }, {} as Record<string, any>);
  }

  return obj;
}

function removeDuplicateFeatures(promptResults: PromptResultCreate[]): PromptResultCreate[] {
  const groupedResults = groupBy(promptResults, 'feature');

  return Object.values(groupedResults).map((entries) => {
    const visibleEntry = entries.find((entry) => !entry.isHidden);
    return visibleEntry || entries[0];
  });
}

function getPromptResultsReport({
  formState,
  suggestedValueProps,
  submissionId,
  entityId,
  getValue,
}: PromptResultsReportParams): PromptResultCreate[] {
  const promptResults: PromptResultCreate[] = [];

  Object.keys(suggestedValueProps).forEach((suggestedField) => {
    const suggestedValueProp = suggestedValueProps[suggestedField];
    if (isNil(suggestedValueProp) || suggestedValueProp.promptResultFeature === null) {
      return;
    }

    const convertValueToString = (value: any): string => {
      if (isNil(value)) {
        return '';
      }

      const normalizedValue = normalizeAndSortObject(value);

      // handle valid date strings that are not numeric
      if (!isNumeric(normalizedValue) && new Date(normalizedValue).toString() !== 'Invalid Date') {
        return new Date(normalizedValue).toISOString();
      }

      return isString(normalizedValue) ? normalizedValue : JSON.stringify(normalizedValue);
    };

    const formValue = formState[suggestedField];
    const getModifiedResult = () => {
      const value = getValue ? getValue(formValue) : formValue;
      if (isArray(value)) {
        return convertValueToString(value.filter((val) => !isNil(val)));
      }
      return convertValueToString(value);
    };

    promptResults.push({
      result: convertValueToString(suggestedValueProp.suggestion.value),
      input: {},
      modifiedResult: getModifiedResult(),
      feature: suggestedValueProp.promptResultFeature,
      submissionId,
      isHidden: !(suggestedField in formState),
      entityId,
      resultReason: suggestedValueProp.suggestion.reason,
    });
  });

  return removeDuplicateFeatures(promptResults);
}

export function useSendPromptReport() {
  const { batchCreatePromptResult } = useMutatePromptResult();

  return async (params: PromptResultsReportParams) => {
    const promptResults = getPromptResultsReport(params);
    await batchCreatePromptResult.mutateAsync(promptResults);
  };
}
